import type { ReactNode, CSSProperties } from 'react';

interface GlassCardProps {
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
  /** Elevates glow intensity — e.g. for the primary hero card */
  elevated?: boolean;
  /** Makes the card narrower and more compact */
  compact?: boolean;
  title?: string;
}

/**
 * Liquid-glass morphism card.
 * Uses backdrop-filter blur with a translucent background and
 * a subtle inset light border to simulate glass reflection.
 */
export default function GlassCard({
  children,
  style,
  className,
  elevated = false,
  compact = false,
  title,
}: GlassCardProps) {
  const cardStyle: CSSProperties = {
    position: 'relative',
    backgroundColor: elevated
      ? 'rgba(255, 255, 255, 0.075)'
      : 'rgba(255, 255, 255, 0.055)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '24px',
    boxShadow: elevated
      ? [
          '0 0 0 1px rgba(255,255,255,0.04)',
          '0 4px 6px rgba(0,0,0,0.2)',
          '0 16px 48px rgba(0,0,0,0.5)',
          '0 0 120px rgba(139,92,246,0.12)',
          'inset 0 1px 0 rgba(255,255,255,0.15)',
          'inset 0 -1px 0 rgba(255,255,255,0.03)',
        ].join(', ')
      : [
          '0 0 0 1px rgba(255,255,255,0.03)',
          '0 4px 6px rgba(0,0,0,0.15)',
          '0 12px 36px rgba(0,0,0,0.4)',
          'inset 0 1px 0 rgba(255,255,255,0.1)',
        ].join(', '),
    padding: compact ? '28px 32px' : '48px 44px',
    overflow: 'hidden',
    ...style,
  };

  return (
    <div style={cardStyle} className={className}>
      {/* Top-edge light shimmer */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: '10%',
          right: '10%',
          height: '1px',
          background:
            'linear-gradient(90deg, transparent, rgba(255,255,255,0.25) 40%, rgba(255,255,255,0.25) 60%, transparent)',
          pointerEvents: 'none',
        }}
      />
      {title && (
        <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', marginBottom: '16px' }}>
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}
